import { useEffect, useRef, useState } from "react";
import { MessageSquare, RotateCcw, ScrollText } from "lucide-react";
import { FLOW_MESSAGES, promptGroups } from "../data/flowMessages";
import LabCommentSuggest from "./LabCommentSuggest";

function formatWhen(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("fr-CA", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function LabPromptsPanel({ revisions, studentName, onClose }) {
  const groups = promptGroups();
  return (
    <div
      id="lab-prompts-panel"
      data-testid="lab-prompts-panel"
      className="absolute right-0 top-full z-20 mt-2 w-[min(28rem,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto rounded-2xl border border-gray-100 bg-white p-3 shadow-xl"
    >
      <p className="px-2 pb-2 text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
        Prompts de chaque étape
      </p>
      <p className="px-2 pb-3 text-[11px] leading-snug text-[#4a5f53]">
        Consigne de chaque étape. Un prompt enregistré remplace l’original. Le texte intégré change le message au rejeu.
      </p>
      {groups.map((flow) => (
        <section key={flow.flowId} className="mb-3 last:mb-0">
          <p className="px-2 py-1 text-xs font-semibold text-[#122118]">{flow.title}</p>
          {flow.steps.map((step) => (
            <div key={step.label} className="mb-2">
              <p className="px-2 text-[11px] font-bold text-[#189346]">
                {step.step + 1}. {step.label}
              </p>
              {step.messages.map((message) => (
                <PromptCard
                  key={message.key}
                  message={message}
                  override={revisions.overrides[message.key]}
                  promptOverride={revisions.promptOverrides?.[message.key]}
                  studentName={studentName}
                />
              ))}
            </div>
          ))}
          {flow.extras.length > 0 && (
            <div className="mb-2">
              <p className="px-2 text-[11px] font-bold text-[#829C8D]">Réponses hors bouton</p>
              {flow.extras.map((message) => (
                <PromptCard
                  key={message.key}
                  message={message}
                  override={revisions.overrides[message.key]}
                  promptOverride={revisions.promptOverrides?.[message.key]}
                  studentName={studentName}
                />
              ))}
            </div>
          )}
        </section>
      ))}
      <button type="button" className="sr-only" onClick={onClose}>
        Fermer
      </button>
    </div>
  );
}

function PromptCard({ message, override, promptOverride, studentName }) {
  const original = message.text({ studentName, subject: "cette matière", minutes: 25 });
  const integrated = typeof override === "string";
  const promptRevised = typeof promptOverride === "string";
  return (
    <article className="mx-1 mt-1 rounded-xl bg-[#F7FAF8] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-[#122118]">{message.label}</h3>
        <span className="flex shrink-0 gap-1">
          {promptRevised && (
            <span className="rounded-full bg-[#E7F0FF] px-2 py-0.5 text-[10px] font-bold text-[#1D4E89]">
              Prompt modifié
            </span>
          )}
          {integrated && (
            <span className="rounded-full bg-[#E9FBF0] px-2 py-0.5 text-[10px] font-bold text-[#0B8F6A]">
              Intégré
            </span>
          )}
        </span>
      </div>
      <p className="mt-1 text-[11px] leading-snug text-[#4a5f53]">
        <span className="font-semibold text-[#829C8D]">Prompt · </span>
        {promptRevised ? promptOverride : message.prompt}
      </p>
      <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-[#122118]">
        <span className="font-semibold text-[#829C8D]">
          {integrated ? "Texte intégré · " : "Message · "}
        </span>
        {integrated ? override : original}
      </p>
    </article>
  );
}

const COPY_FIELDS = [
  ["{enfant}", "Prénom de l’enfant (messages au parent)"],
  ["{student}", "Prénom de l’élève"],
  ["{parent}", "Prénom du parent"],
  ["{tuteur}", "Nom du tuteur virtuel"],
];

const LabCopyStudio = ({
  targetKey,
  followLive,
  resolvedText,
  originalText,
  revised,
  resolvedPrompt,
  originalPrompt,
  promptRevised,
  integrations,
  savedComment = "",
  onCommentChange,
  onSelectTarget,
  onFollowLive,
  onSave,
  onSavePrompt,
  onRevert,
  onRevertPrompt,
  onClearAll,
  onIntegrated,
}) => {
  const editRef = useRef(null);
  const promptRef = useRef(null);
  const [draft, setDraft] = useState(resolvedText);
  const [promptDraft, setPromptDraft] = useState(resolvedPrompt);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(resolvedText);
    setError("");
  }, [targetKey, resolvedText]);

  useEffect(() => {
    setPromptDraft(resolvedPrompt);
    setError("");
  }, [targetKey, resolvedPrompt]);

  useEffect(() => {
    setNotice("");
  }, [targetKey]);

  const flowIntegrations = integrations;
  const stepLabel = FLOW_MESSAGES.find((item) => item.key === targetKey)?.label || targetKey;

  const insertField = (token) => {
    const el = editRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    setDraft(`${draft.slice(0, start)}${token}${draft.slice(end)}`);
    setNotice("");
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = () => {
    setNotice("");
    const result = onSave(draft);
    if (!result?.ok) {
      setError(result?.reason || "Le texte n’a pas été enregistré.");
      return;
    }
    setError("");
    setNotice(result.summary || "Texte enregistré pour les prochains rejeux.");
  };

  const savePrompt = () => {
    setNotice("");
    const result = onSavePrompt(promptDraft);
    if (!result?.ok) {
      setError(result?.reason || "Le prompt n’a pas été enregistré.");
      return;
    }
    setError("");
    setNotice(result.summary || "Prompt enregistré.");
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4" data-testid="lab-copy-studio">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          <MessageSquare className="w-4 h-4 text-[#189346]" />
          Messages, prompts et commentaires
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-[#4a5f53]">
          <input
            type="checkbox"
            data-testid="lab-follow-live"
            checked={followLive}
            onChange={(event) => onFollowLive(event.target.checked)}
          />
          Suivre l’étape
        </label>
      </div>

      <label className="mt-3 block text-[10px] font-bold uppercase tracking-wider text-[#829C8D]" htmlFor="lab-message-target">
        Message
      </label>
      <select
        id="lab-message-target"
        data-testid="lab-message-target"
        value={targetKey}
        onChange={(event) => onSelectTarget(event.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 bg-[#FBFCFC] px-3 py-2 text-sm outline-none focus:border-[#25D366]"
      >
        {[1, 10, 2, 3, 4, 5, 6, 7, 8, 9].map((id) => (
          <optgroup key={id} label={`Flow ${id}`}>
            {FLOW_MESSAGES.filter((item) => item.flowId === id).map((item) => (
              <option key={item.key} value={item.key}>
                {item.step == null ? "•" : item.step + 1}. {item.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      <label className="mt-3 block text-[10px] font-bold uppercase tracking-wider text-[#829C8D]" htmlFor="lab-prompt-edit">
        Prompt
      </label>
      <textarea
        id="lab-prompt-edit"
        ref={promptRef}
        data-testid="lab-prompt-edit"
        value={promptDraft}
        rows={4}
        onChange={(event) => {
          setPromptDraft(event.target.value);
          setNotice("");
        }}
        className="mt-1 w-full resize-y rounded-xl border border-gray-200 bg-[#FBFCFC] px-3 py-2.5 text-sm outline-none focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="lab-prompt-save"
          onClick={savePrompt}
          className="rounded-full bg-[#122118] px-4 py-2 text-xs font-semibold text-white"
        >
          Enregistrer le prompt
        </button>
        {promptRevised && (
          <button
            type="button"
            data-testid="lab-prompt-revert"
            onClick={() => onRevertPrompt(targetKey)}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 text-xs font-semibold text-[#4a5f53] hover:bg-gray-50"
          >
            <RotateCcw className="w-3 h-3" />
            Prompt d’origine
          </button>
        )}
      </div>
      {promptRevised && originalPrompt !== resolvedPrompt && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] font-semibold text-[#829C8D]">
            Voir le prompt d’origine
          </summary>
          <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-[#4a5f53]">
            {originalPrompt}
          </p>
        </details>
      )}

      <label className="mt-3 block text-[10px] font-bold uppercase tracking-wider text-[#829C8D]" htmlFor="lab-message-edit">
        Texte du message
      </label>
      <textarea
        id="lab-message-edit"
        ref={editRef}
        data-testid="lab-message-edit"
        value={draft}
        rows={5}
        onChange={(event) => {
          setDraft(event.target.value);
          setNotice("");
        }}
        className="mt-1 w-full resize-y rounded-xl border border-gray-200 bg-[#FBFCFC] px-3 py-2.5 text-sm outline-none focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20"
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[11px] text-[#829C8D]" data-testid="lab-fields">
        Champs :
        {COPY_FIELDS.map(([token, hint]) => (
          <button
            key={token}
            type="button"
            title={hint}
            data-testid={`lab-field-${token.slice(1, -1)}`}
            onClick={() => insertField(token)}
            className="rounded-full border border-[#cfe9d8] bg-[#F1FBF5] px-2 py-0.5 font-mono text-[11px] text-[#189346] hover:bg-[#E9FBF0]"
          >
            {token}
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="lab-message-save"
          onClick={save}
          className="rounded-full bg-[#122118] px-4 py-2 text-xs font-semibold text-white"
        >
          Enregistrer le texte
        </button>
        {revised && (
          <button
            type="button"
            data-testid="lab-message-revert"
            onClick={() => onRevert(targetKey)}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 text-xs font-semibold text-[#4a5f53] hover:bg-gray-50"
          >
            <RotateCcw className="w-3 h-3" />
            Texte d’origine
          </button>
        )}
      </div>
      {revised && originalText !== resolvedText && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] font-semibold text-[#829C8D]">
            Voir l’original
          </summary>
          <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-[#4a5f53]">
            {originalText}
          </p>
        </details>
      )}

      {(error || notice) && (
        <p
          className={`mt-2 text-xs ${error ? "text-red-600" : "font-semibold text-[#0B8F6A]"}`}
          role={error ? "alert" : "status"}
          data-testid="lab-edit-status"
        >
          {error || notice}
        </p>
      )}

      <LabCommentSuggest
        key={targetKey}
        prompt={promptDraft}
        message={draft}
        label={stepLabel}
        initialComment={savedComment}
        onCommentChange={onCommentChange}
        onUsePrompt={(text) => {
          setPromptDraft(text);
          return onSavePrompt(text);
        }}
        onUseMessage={(text) => {
          setDraft(text);
          return onSave(text);
        }}
        onIntegrated={onIntegrated}
      />

      <div className="mt-4" data-testid="lab-integrations">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
            Intégrations · {flowIntegrations.length}
          </p>
          {integrations.length > 0 && (
            <button
              type="button"
              data-testid="lab-clear-revisions"
              onClick={onClearAll}
              className="text-[11px] font-semibold text-[#829C8D] hover:text-[#122118]"
            >
              Tout effacer
            </button>
          )}
        </div>
        {flowIntegrations.length === 0 ? (
          <p className="mt-1 text-[11px] text-[#829C8D]">
            Aucun changement pour l’instant. Un commentaire ou une édition apparaîtra ici, puis au rejeu.
          </p>
        ) : (
          <ul className="mt-2 max-h-48 space-y-2 overflow-y-auto">
            {flowIntegrations.map((item) => (
              <li
                key={item.id}
                className="rounded-xl border border-[#C6EBD7] bg-[#F3FCF7] px-2.5 py-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-semibold text-[#122118]">
                    {item.label}
                    <span className="ml-1 font-medium text-[#189346]">· {item.summary}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      item.scope === "prompt" ? onRevertPrompt(item.key) : onRevert(item.key)
                    }
                    className="shrink-0 text-[10px] font-semibold text-[#829C8D] hover:text-[#122118]"
                  >
                    Origine
                  </button>
                </div>
                <p className="mt-0.5 text-[10px] text-[#829C8D]">
                  {item.scope === "prompt"
                    ? item.kind === "prompt"
                      ? "Prompt modifié"
                      : `Prompt · « ${item.comment} »`
                    : item.kind === "edit"
                      ? "Édition directe"
                      : `« ${item.comment} »`}
                  {item.at ? ` · ${formatWhen(item.at)}` : ""}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-[#829C8D] line-through">
                  {item.before}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-[#122118]">
                  {item.after}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-[10px] text-[#829C8D]">
        <ScrollText className="w-3 h-3" />
        Le bouton Prompts liste la consigne de chaque étape. Un prompt enregistré remplace l’original.
      </p>
    </div>
  );
};

export default LabCopyStudio;
